import PropTypes from 'prop-types';
import { sentenceCase } from 'change-case';
import { DetailField, DetailGrid } from '../../../components';
import { fDateTime } from '../../../utils/formatTime';
import { fCurrency } from '../../../utils/formatNumber';

/** Only format when there is a value, so empty fields fall back to a dash. */
const asSentence = (value) => (value ? sentenceCase(value) : null);

/**
 * The read-only field list for a maintenance record.
 *
 * `payments`, `disposals` and `handovers` each carried a byte-identical copy of
 * this markup, so the same three bugs were live in four places: the branch read
 * `asset.location.name` (Asset has no `location` relation — it is `branch`, so
 * the row was permanently blank), "Return Status" was bound to `status` and just
 * repeated "Repair Status", and the values were rendered as disabled TextFields
 * whose labels overlapped them. One definition now.
 */
export default function MaintenanceRecordDetails({ record }) {
  return (
    <DetailGrid>
      <DetailField label="Asset" value={record?.asset?.name} />
      <DetailField label="Branch" value={record?.branch?.name ?? record?.asset?.branch?.name} />
      <DetailField label="Issued By" value={record?.staff?.user?.display_name} />
      {/* An in-house job has no provider on purpose; without this it reads the
          same as a record whose provider was deleted. */}
      <DetailField
        label="Service Provider"
        value={record?.in_house ? 'In-house (our own team)' : record?.service_provider?.display_name}
      />
      <DetailField
        label="Service Provider Contact"
        value={record?.in_house ? record?.staff?.user?.mobile_number : record?.service_provider?.mobile_number}
      />
      <DetailField label="Job Card Number" value={record?.job_card_number} placeholder="Not issued yet" />
      <DetailField label="Repair Type" value={asSentence(record?.repair_type)} />
      <DetailField label="Repair Date" value={record?.created_at ? fDateTime(record.created_at) : null} />
      <DetailField label="Repair Status" value={asSentence(record?.status)} />
      <DetailField label="Return Status" value={asSentence(record?.asset_return_approval_status)} />
      <DetailField label="Duration (Days)" value={record?.duration} placeholder="Not set" />
      <DetailField label="Estimated Cost" value={record?.estimated_cost ? fCurrency(record.estimated_cost) : null} />
      <DetailField label="Total Cost" value={record?.total_cost ? fCurrency(record.total_cost) : null} />
      <DetailField
        label="Description"
        value={record?.description}
        multiline
        sx={{ gridColumn: { sm: '1 / -1' } }}
      />
    </DetailGrid>
  );
}

MaintenanceRecordDetails.propTypes = {
  record: PropTypes.object,
};

MaintenanceRecordDetails.defaultProps = {
  record: null,
};
